import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ScrollText, AlertTriangle, Trophy, Calendar, Users, Plus, Edit3, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RulesPage() {
  const { isAdmin } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({ rules_title: 'Tournament Rules', rules_subtitle: 'Official Guidelines' });
  const [editRule, setEditRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({ title: '', description: '' });
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    fetchRules();
    fetchContent();
  }, []);

  const fetchRules = async () => {
    try {
      const { data } = await axios.get(`${API}/rules`);
      setRules(data);
    } catch (err) {
      console.error('Error fetching rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchContent = async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      if (data.rules_title) setContent(c => ({ ...c, rules_title: data.rules_title }));
      if (data.rules_subtitle) setContent(c => ({ ...c, rules_subtitle: data.rules_subtitle }));
    } catch {}
  };

  const openCreate = () => {
    setEditRule(null);
    setRuleForm({ title: '', description: '' });
    setCreateOpen(true);
  };

  const openEdit = (rule) => {
    setEditRule(rule);
    setRuleForm({ title: rule.title, description: rule.description });
    setCreateOpen(true);
  };

  const handleSaveRule = async (e) => {
    e.preventDefault();
    try {
      if (editRule) {
        await axios.put(`${API}/rules/${editRule.id}`, ruleForm);
        toast.success('Rule updated!');
      } else {
        await axios.post(`${API}/rules`, ruleForm);
        toast.success('Rule added!');
      }
      setCreateOpen(false);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/rules/${ruleId}`);
      toast.success('Rule deleted');
      fetchRules();
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  };

  const icons = [ScrollText, Users, AlertTriangle, Calendar, Trophy];

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="rules-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-16">
          <div className="text-center flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-4">
              {content.rules_subtitle}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white mb-4">
              {content.rules_title}
            </h1>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              All participants must adhere to these rules. Violations may result in penalties or disqualification.
            </p>
          </div>
          {isAdmin && (
            <Button
              onClick={openCreate}
              className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none flex-shrink-0"
              data-testid="add-rule-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Rule
            </Button>
          )}
        </div>

        {/* Rules */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {rules.map((rule, idx) => {
              const Icon = icons[idx % icons.length];
              const isLarge = idx === 0 || idx === 4;
              
              return (
                <div
                  key={rule.id}
                  className={`bg-[#121212] border border-white/5 p-8 card-hover relative ${isLarge ? 'md:col-span-2' : ''}`}
                  data-testid={`rule-card-${rule.number}`}
                >
                  {isAdmin && (
                    <div className="absolute top-4 right-4 flex gap-3">
                      <button
                        onClick={() => openEdit(rule)}
                        className="w-8 h-8 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center text-cyan-400 hover:bg-cyan-400 hover:text-black transition-all"
                        data-testid={`edit-rule-${rule.id}`}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.id)}
                        className="w-8 h-8 bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-all"
                        data-testid={`delete-rule-${rule.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <span className="font-mono text-5xl sm:text-6xl font-bold text-zinc-800">
                        {String(rule.number).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="flex-1 pt-2">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-cyan-400" />
                        </div>
                        <h2 className="font-heading text-xl sm:text-2xl font-bold text-white uppercase">
                          {rule.title}
                        </h2>
                      </div>
                      <p className="text-zinc-400 leading-relaxed">
                        {rule.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Important Notice */}
        <div className="mt-12 bg-yellow-500/10 border border-yellow-500/30 p-6" data-testid="rules-notice">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-heading text-lg font-bold text-yellow-500 uppercase mb-2">
                Important Notice
              </h3>
              <p className="text-zinc-400">
                Tournament administrators reserve the right to modify rules as needed. 
                All decisions made by officials are final.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Rule Create/Edit Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase">
              {editRule ? 'Edit Rule' : 'Add Rule'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveRule} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Title</Label>
              <Input
                value={ruleForm.title}
                onChange={(e) => setRuleForm({ ...ruleForm, title: e.target.value })}
                placeholder="Rule title"
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                data-testid="rule-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Description</Label>
              <Textarea
                value={ruleForm.description}
                onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                placeholder="Rule description..."
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[100px] focus:border-cyan-400 resize-none"
                data-testid="rule-description-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
              data-testid="save-rule-button"
            >
              {editRule ? 'Update Rule' : 'Add Rule'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
